
import {
  PrimaryButton,
  CQTreeTable,
  DotMenu,
  usePaginationSessionStorage
} from '@laxmimanogna/code-quick-components';

import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Text,
  Tooltip,
  useBoolean,
  Checkbox,
  Spacer,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { toTitleCase } from '../../../../utils/common.utils';
import {
  CHART_SUBHEADER,
  ChartStatusBadge,
  BatchStatusIndicator,
  AttachmentCell,
  CompletedDateCell,
  getBatchStatus,
} from './table_config';
import { replaceRoute } from '../../../../utils/common.utils';
import AppColors from '../../../../constants/AppColors';
import { format } from 'date-fns';
import { AuditContext } from '../../../../providers/AuditProvider';
import { AuthContext } from '../../../../providers/AuthProvider';
import { createTreeTableProps, paginationProps } from '../../../../utils/cq_tree_table_props';
import { useNavigate } from 'react-router-dom';
import ROUTES from '../../../../constants/Routes';
import { DotMenuIcon } from '../../../../constants/IconData';
import AuditSheetFileViewer from '../../../audit_sheet/components/AuditSheetFileViewer'
import NotesDrawer from '../../../audit_sheet/components/NotesDrawer';
import { MERGEABLE_CHART_STATUSES } from '../../../../constants/constants';
import { EyeAvatarIcon } from '../../../../constants/IconData';
import UrgentConfirmation from '../confirmation_modals/UrgentConfirmation';
import DeleteConfirmation from '../confirmation_modals/DeleteConfirmation';
import { SplitAuditModal } from '../SplitAuditModal';
import AddAdditionalDocsModal from '../AddAdditionalDocsModal';
import MergeChartsModal from '../MergeChartsModal';
import AuditModal from '../../../../components/modal/audit_modal/AuditModal';
import DeleteConfirmationPopUp from '../../../../components/modal/delete_confirmation_modal/DeleteConfirmationPopUp';
import { ClientType } from '../../../../utils/clientTypeUtils';
import AssignChartsModal, { ASSIGN_FIELDS } from '../AssignChartsModal';


const columnHelper = createColumnHelper();

const NewAuditTable = ({
  onOpenNotes, 
  PAGE_NAME, 
  TABLE_NAME, 
  filterResetTrigger,
  tableToggleData
}) => {
    const auditContext = useContext(AuditContext);
    const authContext = useContext(AuthContext);
    const { 
        allCharts, 
        isAllChartsLoading,
        getAllCharts, 
        filterParams,
        handleApplyFilter,
        getAuditUpload
    } = auditContext;

    const {
      getPaginationSession,
      setPaginationSession
    } = usePaginationSessionStorage();

    const [highlightedRowId, setHighlightedRowId] = useState(null);
    const navigate = useNavigate();
    const [ordering, setOrdering] = useState(getPaginationSession(PAGE_NAME, TABLE_NAME)?.ordering || '');
    const tableRef = React.useRef(null);
    const [isOpen, ioState] = useBoolean(false);
    const [modalBatchId, setModalBatchId] = useState(null);
    const [assignField, setAssignField] = useState(ASSIGN_FIELDS.SPECIALTY);

    const openAssignModal = (field, batchUploadId) => {
      setAssignField(field);
      setModalBatchId(batchUploadId);
      ioState.on();
    };

    const [selectedRowId, setSelectedRowId] = useState('');
    const [reqMissingBool, setReqMissingBool] = useBoolean(false);
    const [showSplitModal, ssmState] = useBoolean(false);
    const [showDeleteConfirmationModal, sdcState] = useBoolean(false);
    const [showAddDocsModal, sadmState] = useBoolean(false);
    const [currentSplitUpload, setCurrentSplitUpload] = useState({});
    const [currentAuditUpload, setCurrentAuditUpload] = useState({});
    const [textArea, setTextArea] = useState('');
    const [textAreaValidation, setTextAreaValidation] = useBoolean(false);

    const [selectedBatchRowId, setSelectedBatchRowId] = useState(null);
    const [selectedChartIds, setSelectedChartIds] = useState(() => new Set());
    const [showMergeModal, smmState] = useBoolean(false);

    useEffect(() => {
        if (tableToggleData.expand) {
          (allCharts ?? []).forEach((_, idx) => tableRef.current.expandRow(String(idx)))
        }
      }, [tableToggleData.expand]);

      useEffect(() => {
        if (tableToggleData.collapse) {
          tableRef.current.collapseAll();
        }
      }, [tableToggleData.collapse])

    useEffect(() => {
      if (filterResetTrigger === null) {
        return;
      }
      setOrdering(null);
      tableRef.current?.setCustomSorting();
    }, [filterResetTrigger])

    useEffect(() => {
      if (!ordering) {
        return;
      }
      let rev = false, key = ordering;
      if (ordering[0] === "-") {
        rev = true;
        key = ordering.substring(1);
      }

      tableRef?.current.setCustomSorting([{'id': key, desc: rev}])
    }, [])

    const clearChartSelection = () => {
      setSelectedBatchRowId(null);
      setSelectedChartIds(new Set());
    };

    // Selection references batches/charts by row index, so a pagination,
    // sort, search, or filter-tab change (anything that reloads allCharts)
    // can leave it pointing at the wrong batch - drop any in-progress merge
    // selection whenever the underlying data changes.
    useEffect(() => {
      clearChartSelection();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allCharts]);

    const toggleChartSelection = (batchRowId, chartId) => {
      setSelectedChartIds(prev => {
        const next = new Set(prev);
        if (next.has(chartId)) {
          next.delete(chartId);
        } else {
          next.add(chartId);
        }
        setSelectedBatchRowId(next.size === 0 ? null : batchRowId);
        return next;
      });
    };

    const toggleBatchSelection = (batchRowId, chartIds, checked) => {
      if (checked) {
        setSelectedChartIds(new Set(chartIds));
        setSelectedBatchRowId(batchRowId);
      } else {
        clearChartSelection();
      }
    };

    // A chart can only be merged before auditing has actually started - see
    // MERGEABLE_CHART_STATUSES for why ON HOLD is excluded. has_audit_sheet_data
    // covers charts whose data was auto-extracted from an upload template -
    // the merge endpoint enforces this too, but checking it here avoids
    // sending the manager through the whole select/pick-primary/merge flow
    // just to be rejected at the last step.
    const isChartMergeable = chart =>
      MERGEABLE_CHART_STATUSES.includes(chart?.status) && !chart?.has_audit_sheet_data;

    // Arms selection mode for a batch (checkboxes only render for that batch's
    // rows) and pre-checks the chart the user triggered it from.
    const handleSelectToMerge = (chart, batchRowId) => {
      setSelectedBatchRowId(batchRowId);
      setSelectedChartIds(new Set([chart.id]));
      tableRef.current?.expandRow(String(batchRowId));
    };

    const selectedCharts = useMemo(() => {
      if (selectedBatchRowId === null || selectedChartIds.size === 0) {
        return [];
      }
      const batch = allCharts?.[selectedBatchRowId];
      if (!batch) {
        return [];
      }
      return (batch.subRows || []).filter(chart => selectedChartIds.has(chart.id));
    }, [allCharts, selectedBatchRowId, selectedChartIds]);

    function handleSplitAudit(row) {
      ssmState.on();
      setCurrentSplitUpload(row);
    }
    
    function handleAddAdditionalDocs(row) {
      sadmState.on();
      setCurrentAuditUpload(row);
    }

    function handleDeleteUpload(row) {
      sdcState.on();
      setCurrentAuditUpload(row);
    }

    const requestMissingInfo = async () => {
      if (textArea?.length === 0 || textArea === ' ') {
        setTextAreaValidation.on();
      } else {
        await auditContext.requestMissingInfo(selectedRowId, textArea);
        setReqMissingBool.off();
      }
    };

    const RenderActionComponent = ({
      auditUpload,
      batchRowId,
      onClickingFileViewer = () => {},
      onClosingFileViewer = () => {},
    }) => {
      return (
        <HStack>
          <AuditSheetFileViewer
            auditData={auditUpload}
            icon={<EyeAvatarIcon 
              style={{ cursor: 'pointer'}} 
              onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}/>}
            chart_id={auditUpload.id}     
            onOpen={onClickingFileViewer}
            onClose={onClosingFileViewer}
          />
          <DotMenu
            icon={
              <DotMenuIcon
                style={{ width: '5px', height: '27px', color: '#D8D8D8' }}
              />
            }
            menuDataItem={[
              {
                onClick: () => handleAddAdditionalDocs(auditUpload),
                children: (
                  <Box width="100%" p="5px" color={'black'}>
                    Add additional documents
                  </Box>
                ),
              },
              {
                isDisabled: !isChartMergeable(auditUpload),
                onClick: () => handleSelectToMerge(auditUpload, batchRowId),
                children: (
                  <Tooltip
                    hasArrow
                    placement="top"
                    label={
                      isChartMergeable(auditUpload)
                        ? ''
                        : "Only charts awaiting assignment or audit, with no audit sheet data yet, can be merged"
                    }
                  >
                    <Box width="100%" p="5px" color={'black'}>
                      Select to merge
                    </Box>
                  </Tooltip>
                ),
              },
              {
                isDisabled:
                  !!auditUpload.is_splitted || auditUpload.total_page == 0,
                onClick: () => handleSplitAudit(auditUpload),
                children: (
                  <Box width="100%" p="5px" color={'black'}>
                    Split Audit (Don't use)
                  </Box>
                ),
              },
              {
                children: <UrgentConfirmation data={auditUpload} />,
              },
              {
                onClick: () => {
                  setReqMissingBool.on();
                  setSelectedRowId(auditUpload.id);
                },
                children: (
                  <div
                    style={{ color: 'black', width: '100%' }}
                  >
                    Request Missing Info
                  </div>
                ),
              },
              {
                onClick: () => handleDeleteUpload(auditUpload),
                children: (
                  <Text
                  p="5px"
                  width="100%"
                  style={{ color: 'red' }}
                  >
                    Delete Upload
                  </Text>
                ),
              },
            ]}
          />
        </HStack>
      );
    };

    const ClickableCell = ({ onClick, children }) => {
      const handleMouseEnter = (e) => {
        e.currentTarget.style.background = '#F3F4F6';
        e.currentTarget.style.textDecoration = 'underline';
      };

      const handleMouseLeave = (e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.textDecoration = 'none';
      };

      return (
        <div
          onClick={onClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            width: '100%',
            height: '100%',
            padding: '12px 2px',
            cursor: 'pointer',
            transition: 'background 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </div>
      );
    };

    // keeps the batch data in session storage so that we can 
    // highlight this item from the table after returning to this page
    const onBatchNavigation = (
      batchRowId,
      batchId
    ) => {
      getAllCharts.saveNavigationState(
        PAGE_NAME,
        TABLE_NAME,
        [batchRowId, batchId]
      );
    }
    const onChartNavigation = (
    batchRowId,
    chartRowId,
    batchId,
    chartId
  ) => {
    getAllCharts.saveNavigationState(
      PAGE_NAME,
      TABLE_NAME,
      [
        batchRowId,
        chartRowId, 
        batchId,
        chartId
      ]
    );
  }

  const initializingNavState = () => {
    if ((allCharts ?? []).length > 0) {
      const itemId = getAllCharts.restoreNavigationState(PAGE_NAME, TABLE_NAME, true);
      if (itemId) {
        const navData = itemId.split(',').map(Number);
        // for viewing batch 
        if (navData.length === 2) {
          const [batchRow, batchId] = navData;
          if (allCharts?.[batchRow]?.upload_id == batchId) {
            setHighlightedRowId(batchId);
          } 
        } else {
          // for viewing chart 
          const [batchRow, chartRow , batchId, chartId] = navData;
          if (allCharts?.[batchRow]?.upload_id == batchId && allCharts?.[batchRow]?.subRows?.[chartRow].id === chartId) {
            tableRef.current.expandRow(String(batchRow));
            setHighlightedRowId(chartId);
          }
        }             
                          
        setTimeout(() => {
          setHighlightedRowId(null);
        }, 2000);
      }
    }
  }


    useEffect(() => {
      initializingNavState();
    }, [allCharts]);
    
    useEffect(() => {
        let searching = !!(filterParams.searchText && filterParams.searchText.trim());
        getAllCharts.loadAPI(PAGE_NAME, TABLE_NAME, searching);
    }, [filterParams.ordering, filterParams.chartStatusType, filterParams.triggerSearch, getAllCharts.showOrdering]);

    useEffect(() => {
      if ((ordering || filterParams.ordering) && ordering !== filterParams.ordering) {
        setPaginationSession(PAGE_NAME, TABLE_NAME, {
          ...getPaginationSession(PAGE_NAME, TABLE_NAME),
          page: 1,
          size: 10
        })
        handleApplyFilter({
          ...filterParams,
          ordering: ordering
        })
      }
    }, [ordering]);


    const columns = useMemo(() => [

        // ── Col 0: Selection checkbox (for merging charts within a batch) ─────────
        columnHelper.display({
          id: 'select',
          header: () => '',
          cell: info => {
            // Selection mode is armed per-batch via a chart's "Select to merge"
            // menu item (handleSelectToMerge). Outside the armed batch, this
            // column renders nothing — no checkboxes clutter the default view.
            const isBatch = info.row.depth === 0;

            if (isBatch) {
              if (selectedBatchRowId !== info.row.index) {
                return null;
              }
              const subRows = info.row.original.subRows || [];
              const eligibleChartIds = subRows.filter(isChartMergeable).map(c => c.id);
              if (!eligibleChartIds.length) {
                return null;
              }
              const selectedCount = eligibleChartIds.filter(id => selectedChartIds.has(id)).length;
              const allSelected = selectedCount === eligibleChartIds.length;
              return (
                <Checkbox
                  isChecked={allSelected}
                  isIndeterminate={selectedCount > 0 && !allSelected}
                  onChange={e => toggleBatchSelection(info.row.index, eligibleChartIds, e.target.checked)}
                />
              );
            }

            const batchRowId = info.row.getParentRow().index;
            if (selectedBatchRowId !== batchRowId) {
              return null;
            }
            const chart = info.row.original;
            const chartId = chart.id;
            const mergeable = isChartMergeable(chart);
            return (
              <Tooltip
                hasArrow
                placement="top"
                label={
                  mergeable
                    ? ''
                    : "Only charts awaiting assignment or audit, with no audit sheet data yet, can be merged"
                }
              >
                <Box display="inline-block">
                  <Checkbox
                    isChecked={selectedChartIds.has(chartId)}
                    isDisabled={!mergeable}
                    onChange={() => toggleChartSelection(batchRowId, chartId)}
                  />
                </Box>
              </Tooltip>
            );
          },
        }),

        // ── Col 1: Batch / Chart ID ───────────────────────────────────────────────
        // subHeader value is an object → enables custom bg/color AND sortable toggle
        columnHelper.accessor('name', {
          id: 'name',
          header: () => 'Batch',
          subHeader: {
            1: { label: 'File', ...CHART_SUBHEADER, sortable: true },
          },
          cell: info => {
            const isBatch = info.row.depth === 0;
            const chartData = info.row.original;
            const cellValue = info.getValue();
            const isReceived = chartData.status === 'completed';
            const isTruncated = cellValue?.length > 13;
            const truncatedValue = isTruncated
            ? `${cellValue.slice(0, 10)}...`
            : cellValue;
            if(isBatch){
                return (
                    <Tooltip
                    hasArrow
                    backgroundColor={!isReceived ? AppColors.secondary : undefined}
                    placement="bottom"
                    label={
                        isReceived
                        ? (isTruncated ? cellValue : '') // show only if truncated
                        : (
                            <>
                                Batch pending review.
                                {isTruncated && (
                                <>
                                    <br />
                                    {cellValue}
                                </>
                                )}
                            </>
                            )
                    }
                    >
                    <Text
                        whiteSpace="nowrap"
                        _hover={isReceived ? { textDecoration: 'underline' } : {}}
                        cursor={'pointer'}
                        fontWeight={600}
                        color="blue.800"
                        fontSize="14px"
                        textAlign="start"
                    >
                        {truncatedValue}
                    </Text>
                    </Tooltip>
                );
            }else{
              return <Text fontWeight={500} textAlign="start" color="blue.700" fontSize="13px">{info.getValue()}</Text>;
            }
          },
        }),

        // ── Col 2: Uploader Account ──────────────────────
        columnHelper.accessor('uploader', {
          id: 'location',
          header: () => 'Account',
          subHeader: {
            1: { label: 'Hospital', ...CHART_SUBHEADER, sortable: true },
            // sortable omitted → no sort icon on this sub-header column
          },
          sortValue: row => row.hospital?.name ?? '',
          cell: info => {
            const isBatch = info.row.depth === 0;
            const rowData = info.row.getParentRow()?.original;
            if (isBatch) {
              let uploaderDetails = '';
              if ((info.getValue()?.type !== ClientType.PHYSICIANS_GROUP || !info.row.original.hospital) && info.getValue()?.health_system) {
                uploaderDetails = `${info.getValue()?.health_system?.name} -`;
              }
              if (info.row.original.hospital) {
                uploaderDetails = `${uploaderDetails} ${info.row.original.hospital?.name} - ${info.row.original.hospital?.state_abbr} -`
              } else if (info.row.original.uploader?.health_system?.state_abbr) {
                uploaderDetails = `${uploaderDetails} ${info.row.original.uploader?.health_system?.state_abbr} -`
              }
              uploaderDetails = `${uploaderDetails} (${info.getValue()?.full_name})`
             return (
             <ClickableCell 
               onClick={() => openAssignModal(ASSIGN_FIELDS.HOSPITAL, info.row.original.upload_id)}
                >
                  <AttachmentCell
                   attachments={[uploaderDetails]} defaultMsg={"Assign"}/>
                </ClickableCell>
               )    
            } else {
              return <Text textAlign="center" fontSize="13px">{rowData?.hospital?.name} - {rowData?.hospital?.state_abbr}</Text>
            }
          },
        }),
    
        // ── Col 2: Upload Date (batch) / Attachments (chart) ──────────────────────
        columnHelper.accessor('upload_date', {
          id: 'upload_date',
          header: () => 'Upload Date',
          subHeader: {
            1: { label: 'Attachments', ...CHART_SUBHEADER },
            // sortable omitted → no sort icon on this sub-header column
          },
          cell: info => {
            const isBatch = info.row.depth === 0;
            const attachmentNames = [
              info.row.original.file_obj?.name,
              info.row.original.file_obj?.audited_chart_name,
              ...(info.row.original.attachments?.map(a => a.file.name) ?? [])
            ].filter(Boolean);
            if (isBatch) {
              const val = info.getValue();
              return val
                ? <Text textAlign="center" fontSize="14px" whiteSpace={"nowrap"}>{format(new Date(val), 'MM/dd/yyyy')}</Text>
                : <Text color="gray.400" fontSize="14px">N/A</Text>;
            }
            return <AttachmentCell attachments={attachmentNames} />;
          },
        }),
       
    
        // ── Col 3: Status ─────────────────────────────────────────────────────────
        columnHelper.accessor('status', {
          id: 'status',
          header: () => 'Status',
          subHeader: {
            1: { label: 'Status', ...CHART_SUBHEADER, sortable: true },
          },
          cell: info => {
            const isBatch = info.row.depth === 0;
            if (!isBatch) {
            }
            
            return isBatch
              ? <BatchStatusIndicator subRows={info.row.original.subRows} />
              : <ChartStatusBadge status={info.getValue()} />;
          },
        }),
    
        // ── Col 4: Specialty (chart) ─────────────────────
        columnHelper.accessor(row => row, {
          id: 'specialties',
          header: () => 'Specialties',
          subHeader: {
            // Different label at depth 1 — "No. of Charts" becomes "Specialty"
            1: { label: 'Specialty', ...CHART_SUBHEADER, sortable: true },
          },
          // sortValue tells CQTreeTable how to extract a primitive for sub-header sorting
          // accessorFn returns the whole row object which is not directly sortable
          sortValue: row => row.specialty?.name ?? '',
          cell: info => {
            const row     = info.getValue();
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              
              return (
               <ClickableCell 
               onClick={() => openAssignModal(ASSIGN_FIELDS.SPECIALTY, info.row.original.upload_id)}
                >
                  <AttachmentCell
                   attachments={[...new Set((row.subRows || []).map(chart => chart.specialty?.name).filter(val => !!val))]} defaultMsg={"Assign"}/>
                </ClickableCell>     
              )
            }
            if (!row.specialty)
              return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
            return row.specialty?.name
              ? <Text textAlign="center" fontSize="13px">{row.specialty.name}</Text>
              : <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
          },
        }),

        // ── Col 5: Audtiors ─────────────────────
        columnHelper.accessor(row => row, {
          id: 'auditors',
          header: () => 'Auditors',
          subHeader: {
            // Different label at depth 1 — "No. of Charts" becomes "Specialty"
            1: { label: 'Auditor', ...CHART_SUBHEADER, sortable: true },
          },
          // sortValue tells CQTreeTable how to extract a primitive for sub-header sorting
          // accessorFn returns the whole row object which is not directly sortable
          sortValue: row => row.auditor?.full_name ?? '',
          cell: info => {
            const row     = info.getValue();
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              
              return (
                  <ClickableCell
                  onClick={() => openAssignModal(ASSIGN_FIELDS.AUDITOR, info.row.original.upload_id)}>
                  <AttachmentCell 
                  attachments={[...new Set((row.subRows || []).map(chart => chart.auditor?.full_name).filter(val => !!val))]}
                  defaultMsg={"Assign"} />
                </ClickableCell>
              )
            }

            if (!row.auditor)
              return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
            return row.auditor?.full_name
              ? <Text textAlign="center" fontSize="13px">{row.auditor.full_name}</Text>
              : <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
          },
        }),


         // ── Col 6: QA ─────────────────────────────────────────────────
        columnHelper.accessor(row => row, {
          id: 'qas',
          header: () => 'QA',
          subHeader: {
            // Different label at depth 1 — "No. of Charts" becomes "Specialty"
            1: { label: 'QA', ...CHART_SUBHEADER, sortable: true },
          },
          // sortValue tells CQTreeTable how to extract a primitive for sub-header sorting
          // accessorFn returns the whole row object which is not directly sortable
          sortValue: row => row.qa?.full_name ?? '',
          cell: info => {
            const row     = info.getValue();
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              
              return (
                  <ClickableCell
                    onClick={() => openAssignModal(ASSIGN_FIELDS.QA, info.row.original.upload_id)}>
                  <AttachmentCell 
                  attachments={[...new Set((row.subRows || []).map(chart => chart.qa?.full_name).filter(val => !!val))]} 
                  defaultMsg={"Assign"}/>
                </ClickableCell>
              )
          
            }
            if (!row.qa)
              return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
            return row.qa?.full_name
              ? <Text textAlign="center" fontSize="13px">{row.qa.full_name}</Text>
              : <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
          },
        }),
    
    
        // ── Col 7: Providers ────────────────────────────────────────────────────
        columnHelper.accessor(row => row, {
          id: 'providers',
          header: () => 'Providers',
          enableSorting: false,
          subHeader: {
            1: { label: 'Providers', ...CHART_SUBHEADER },
          },
          cell: info => {
            const rowVal = info.getValue();
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              return <AttachmentCell attachments={rowVal.batch_providers} />
            }
            return <AttachmentCell attachments={(rowVal.providers ?? []).map(chart_prov => chart_prov.name)} />
          }
        }),
    
        // ── Col 8: Action — no sub-header label needed ────────────────────────────
        columnHelper.accessor(row => row.upload_id, {
          id: 'action',
          header: () => '',
          enableSorting: false,
          // subHeader intentionally omitted → blank cell in sub-header row
          cell: info => {
            const cellValue = info.getValue();
            const isBatch = info.row.depth === 0;
            const status = info.row.original.status;
            const batchId = info.getValue();
            
            return (
              <HStack justifyContent="start" spacing={2}>
                <Tooltip
                  placement="top"
                  hasArrow
                >
                  <Box>
                    <PrimaryButton
                      bg={AppColors.secondary}
                      fontSize="14px"
                      fontWeight="normal"
                      size="sm"
                      borderRadius="3xl"
                      px={5}
                      _hover={{
                        transition: 'all .1s ease',
                        bgColor: AppColors.white,
                        color: AppColors.secondary,
                        outline: `2px solid ${AppColors.secondary}`
                      }}
                      onClick={() => {    
                        const paginationSession = getPaginationSession(PAGE_NAME, TABLE_NAME);                        
                        setPaginationSession(PAGE_NAME, TABLE_NAME, {...paginationSession, 
                          ...('searchText' in filterParams && {search: filterParams.searchText}),
                          ...(filterParams.ordering && {ordering: filterParams.ordering})})    
                        if (isBatch) {
                          getAllCharts.saveNavigationState(
                            PAGE_NAME,
                            TABLE_NAME,
                            [info.row.index, info.row.original.upload_id]
                          );
                          navigate(
                            ROUTES.VIEW_BATCH_AUDIT_SHEET.replace(':batchId', cellValue)
                          );
                        } else {
                          getAllCharts.saveNavigationState(
                            PAGE_NAME,
                            TABLE_NAME,
                            [
                              info.row.getParentRow().index, 
                              info.row.index, 
                              info.row.getParentRow().original.upload_id, 
                              info.row.original.id
                            ]
                          );
                          const routeFormat = { uploadId: info.row.original.id };
                          const route = ROUTES.VIEW_AUDIT_SHEET;
                          navigate(replaceRoute(route, routeFormat));
                        }
                      }}
                    >
                      {isBatch ? 'View Batch' : 'View File'}
                    </PrimaryButton>
                  </Box>
                </Tooltip>
    
                {!isBatch ? 
                  // <AuditSheetFileViewer
                  //   auditData={info.row.original}
                  //   icon={<EyeAvatarIcon 
                  //     style={{ cursor: 'pointer'}} 
                  //     onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
                  //     onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}/>}
                  //   chart_id={info.row.original.id}     
                  // />
                  <RenderActionComponent
                    auditUpload={info.row.original}
                    batchRowId={info.row.getParentRow().index}
                    onClickingFileViewer = {() => {
                      onChartNavigation(
                        info.row.getParentRow().index,
                        info.row.index,
                        info.row.getParentRow().original.upload_id,
                        info.row.original.id
                      )
                    }}
                    onClosingFileViewer = {initializingNavState}
                  />
                 : (
                <NotesDrawer
                tabType={"ALL"}
                batchId={batchId}
                onOpenNotes={obj => onOpenNotes(obj, batchId, "ALL")}
                style={{
                    width: 35,
                    height: 35,
                    cursor: 'pointer',
                    display: 'inline-block',
                    transition: "0.3s",
                    filter: "invert(1)"
                  }}
                  onOpen={() => {
                    onBatchNavigation(
                      info.row.index,
                      info.row.original.upload_id
                    )
                  }}
                  onClose={initializingNavState} 
                  onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                /> 
                )}
              </HStack>
            );
          }
        }),
    
      ], [isAllChartsLoading, selectedChartIds, selectedBatchRowId]);

    function handlePreviousPage() {
        getAllCharts.loadPrevious(PAGE_NAME, TABLE_NAME);
    }

    function handleNextPage() {
        getAllCharts.loadNext(PAGE_NAME, TABLE_NAME);
    }

    function handleSizeChange(size) {
        getAllCharts.loadSize(PAGE_NAME, TABLE_NAME, size );
    }

    function handlePageChangeByNumber(pageNumber) {
        getAllCharts.loadPageByNumber(PAGE_NAME, TABLE_NAME, pageNumber);
    }
    

    return (
        <Box>
            {selectedBatchRowId !== null && (
              <HStack
                mb={3}
                p={3}
                bg="blue.50"
                border="1px solid"
                borderColor="blue.100"
                borderRadius="md"
                spacing={4}
              >
                <Text fontSize="14px" fontWeight={600} color={AppColors.secondary}>
                  {selectedChartIds.size} chart{selectedChartIds.size === 1 ? '' : 's'} selected
                  {selectedChartIds.size < 2 ? ' · pick at least 2 to merge' : ''}
                </Text>
                <Spacer />
                <Button size="sm" variant="ghost" onClick={clearChartSelection}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  bg={AppColors.secondary}
                  color="white"
                  borderRadius="3xl"
                  px={5}
                  isDisabled={selectedChartIds.size < 2}
                  _hover={{
                    transition: 'all .1s ease',
                    bgColor: AppColors.white,
                    color: AppColors.secondary,
                    outline: `2px solid ${AppColors.secondary}`,
                  }}
                  onClick={smmState.on}
                >
                  Merge Charts
                </Button>
              </HStack>
            )}
            <CQTreeTable
                ref={tableRef}
                data={allCharts}
                columns={columns}
                getSubRows={(row) => row.subRows}
                autoExpand={row => row.expand}
                manualPagination={true}
                manualSortBy={false}
                isLoading={isAllChartsLoading}
                {...createTreeTableProps(highlightedRowId)}
                {...paginationProps(getAllCharts)}
                onAsc={header => {
                setOrdering(header);
                }}
                onDesc={header => {
                setOrdering(`-${header}`);
                }}
                onSortReset={() => {
                  setOrdering(undefined);
                }}
                onPreviousPage={handlePreviousPage}
                onNextPage={handleNextPage}
                onSizeChange={handleSizeChange}
                onPageChangeByNumber={handlePageChangeByNumber}
            />
            <AssignChartsModal
              isOpen={isOpen}
              onClose={ioState.off}
              batchUploadId={modalBatchId}
              focusField={assignField}
            />
            <SplitAuditModal
              isOpen={showSplitModal}
              onClose={ssmState.off}
              currentUpload={currentSplitUpload}
            />

            <AddAdditionalDocsModal
              isOpen={showAddDocsModal}
              onClose={sadmState.off}
              auditUpload={currentAuditUpload}
              onUploadComplete={() => {
                sadmState.off();
                getAuditUpload.loadAPI(PAGE_NAME, TABLE_NAME);
              }}
            />

            <DeleteConfirmationPopUp
              isOpen={showDeleteConfirmationModal}
              onClose={sdcState.off}
              rowId={currentAuditUpload?.id}
              uploadName={currentAuditUpload?.name}
              loadList={() => {
                getAuditUpload.loadAPI(PAGE_NAME, TABLE_NAME);
              }}
            />

            <MergeChartsModal
              isOpen={showMergeModal}
              onClose={smmState.off}
              charts={selectedCharts}
              onMergeComplete={() => {
                smmState.off();
                clearChartSelection();
                getAllCharts.loadAPI(PAGE_NAME, TABLE_NAME);
              }}
            />

            <AuditModal
                isOpen={reqMissingBool}
                onClose={() => setReqMissingBool.off()}
                title={'Audit Hold - Information Request'}
                text={
                  'This will change the audit status to “on hold” until the missing information is received.'
                }
                keyText={'Note : '}
                placeholder={'Chart 12345 is missing information...'}
                height={159}
                width={772}
                submitButton={{
                  children: 'Send',
                  isLoading: auditContext.isReqMissing,
                  loadingText: 'Sending',
                  spinnerPlacement: 'end',
                  disabled: !textArea,
                  onClick: () => {
                    requestMissingInfo();
                  },
                }}
                cancelButton={{
                  children: 'Cancel',
                  onClick: () => {
                    setReqMissingBool.off();
                  },
                }}
                onChange={e => {
                  setTextArea(e.target.value);
                }}
                setAlert={textAreaValidation}
              />
        </Box>
    )
}

export default NewAuditTable;